/**
 * Panel de resultado del cálculo (réplica del panel "Resumen / Ticket" del SSR).
 *
 * Presentacional: recibe el resultado del cálculo y el texto del ticket ya resuelto por el
 * orquestador (técnico para admin; cliente+producción para no-admin, igual que el SSR) y los
 * muestra (total + ticket + imágenes plano/3D + ficha técnica). No hace `fetch` ni cálculo.
 */
import { Button } from "@/components/ui";

import { formatMoney } from "../format";
import type { CalcResult } from "../types";
import { TicketDisplay } from "./TicketDisplay";

interface ResultPanelProps {
  /** Resultado del cálculo (con total, imágenes y ficha), o `null` si aún no se cotizó. */
  result: CalcResult | null;
  /** Texto del ticket a mostrar (lo resuelve el orquestador según el rol). */
  ticketText: string | null;
  /** `true` mientras se calcula / se preparan los tickets. */
  loading: boolean;
  /** Limpia el resultado para volver al estado inicial ("Nueva cotización"). */
  onReset: () => void;
}

export function ResultPanel({ result, ticketText, loading, onReset }: ResultPanelProps) {
  if (loading) {
    return (
      <div className="py-12 text-center" role="status">
        <p className="text-sm text-slate-500">Calculando…</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <h2 className="text-lg font-semibold text-slate-700">
          Complete el formulario y pulse Cotizar
        </h2>
        <p className="max-w-md text-sm text-slate-500">
          Se calculará el precio y el desglose según su rol.
        </p>
      </div>
    );
  }

  const planoSrc = result.imagen_plano
    ? `data:image/png;base64,${result.imagen_plano}`
    : result.imagen_referencia || null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-900 px-4 py-3 text-white">
        <span className="text-sm font-semibold uppercase tracking-wide">Resumen / Ticket</span>
        <div className="flex items-center gap-3">
          <Button type="button" variant="secondary" size="sm" onClick={onReset}>
            Nueva cotización
          </Button>
          <span className="text-lg font-bold">{formatMoney(result.total)}</span>
        </div>
      </div>

      <TicketDisplay ticket={ticketText} />

      {(planoSrc || result.imagen_3d) ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {planoSrc ? (
            <figure className="rounded-md border border-slate-200 p-3 text-center">
              <img src={planoSrc} alt="Plano de la puerta" className="mx-auto max-w-full" />
              <figcaption className="mt-2 text-xs text-slate-500">Plano</figcaption>
            </figure>
          ) : null}
          {result.imagen_3d ? (
            <figure className="rounded-md border border-slate-200 p-3 text-center">
              <img src={result.imagen_3d} alt="Dibujo 3D" className="mx-auto max-w-full" />
              <figcaption className="mt-2 text-xs text-slate-500">Dibujo 3D</figcaption>
            </figure>
          ) : null}
        </div>
      ) : null}

      {result.info_plano ? (
        <details className="rounded-md border border-slate-200 p-3">
          <summary className="cursor-pointer text-sm font-medium text-slate-700">
            Ficha técnica
          </summary>
          <pre className="mt-2 whitespace-pre-wrap font-mono text-xs text-slate-600">
            {result.info_plano}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
